import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dueText } from './reviewUi';

const COLLAPSED = 6;   // two rows of cards on a normal screen

const FILTERS = [
  ['all', 'All', () => true],
  ['late', 'Late', (w) => w.days_left != null && w.days_left < 0],
  ['week', 'Due this week', (w) => w.days_left != null && w.days_left >= 0 && w.days_left <= 7],
  ['new', 'Not opened', (w) => w.my_status !== 'reviewing'],
];

/**
 * "Waiting for you" — the reviews this user owes (D9), soonest due first.
 *
 * Built to hold 20+ without burying the panels below it: a grid of cards, the first six
 * shown and the rest behind "Show all"; quick filters (late / due this week / not opened)
 * whenever there are more than a handful, and a search box once the list is long.
 */
export default function WaitingForYou({ waiting }) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map(([k, , fn]) => [k, waiting.filter(fn).length]),
  ), [waiting]);

  const list = useMemo(() => {
    const fn = (FILTERS.find(([k]) => k === filter) || FILTERS[0])[2];
    const q = query.trim().toLowerCase();
    return waiting.filter(fn).filter((w) => !q
      || `${w.item_key} ${w.item_name} ${w.name} ${w.created_by_name}`.toLowerCase().includes(q));
  }, [waiting, filter, query]);

  if (!waiting.length) return null;
  const many = waiting.length > COLLAPSED;
  const shown = expanded || query ? list : list.slice(0, COLLAPSED);

  return (
    <section aria-label="Waiting for you" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="drv-wait-tools">
        <h2 className="drv-h2">Waiting for you <span>· {waiting.length}</span>
          {counts.late > 0 && <span style={{ color: '#9A3412', fontWeight: 600 }}> · {counts.late} late</span>}
        </h2>
        {many && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="drv-wait-filters" role="tablist" aria-label="Filter reviews waiting for you">
              {FILTERS.map(([k, label]) => (
                <button type="button" role="tab" key={k} aria-selected={filter === k}
                  className={`drv-ftab${filter === k ? ' drv-ftab--on' : ''}`}
                  onClick={() => { setFilter(k); setExpanded(false); }}>
                  {label} {counts[k]}
                </button>
              ))}
            </div>
            <input type="search" className="drv-input" style={{ width: 200 }} placeholder="Search reviews"
              aria-label="Search reviews waiting for you" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="drv-muted" style={{ fontSize: 13 }}>Nothing matches.</div>
      ) : (
        <div className="drv-tasks">
          {shown.map((w) => {
            const due = dueText(w.due_date, w.days_left);
            const p = w.progress || {};
            const state = w.my_status === 'reviewing' ? 'you started' : 'not opened yet';
            return (
              <Link className="drv-task" key={w.id} to={w.link}>
                <div className="drv-task-top">
                  <span className="drv-task-key">{w.item_key} · Rev {w.revision} · Round {w.current_round}</span>
                  <span className={`drv-pill drv-pill--${due.tone === 'late' ? 'late' : 'none'}`}>{due.text}</span>
                </div>
                <div className="drv-task-name">{w.item_name}</div>
                <div className="drv-small">
                  {w.name} · from {w.created_by_name} · {state}
                  {p.comments_total ? ` · ${p.comments_total} comments` : ''}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {!query && list.length > COLLAPSED && (
        <button type="button" className="drv-btn drv-more" onClick={() => setExpanded((x) => !x)}>
          {expanded ? 'Show fewer' : `Show all ${list.length}`}
        </button>
      )}
    </section>
  );
}
